import { FC } from 'react';
import EmissionsCostsChart from './EmissionsCostsChart.component';
import ScoreStressTestChart from './ScoreStressTestChart.component';
import { useAdditionalTitleBarSelect } from '../../../../hooks/useAdditionalTitleBarComponents';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import { Grid } from '@mui/material';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import {
  useEsgStressTestQuery,
  type EsgStressTestPageModel,
  type EsgStressTestPayload,
} from '../../../../api/rs-hg/esg';

interface EmissionsChartData {
  name: string;
  scope1: number;
  scope2: number;
  scope3: number;
  totalC02: number;
  airPol: number;
  airPolName: string;
}

interface ChartDataType {
  name: string;
  score: number;
}

function buildEmissionsChartData(
  data: EsgStressTestPayload,
): EmissionsChartData[] {
  if (!data?.emission_data) return [];
  const emissionData = data.emission_data;
  const chartData: EmissionsChartData[] = [];
  chartData.push({
    name: emissionData['scope 1'][0][0],
    scope1: emissionData['scope 1'][0][1],
    scope2: emissionData['Scope 2'][0][1],
    scope3: emissionData['Scope 3'][0][1],
    totalC02: emissionData['Total CO2'][0][1],
    airPol: emissionData['SOX / NOX etc'][0][1],
    airPolName: emissionData['SOX / NOX etc'][0][0],
  });
  chartData.push({
    name: emissionData['scope 1'][1][0],
    scope1: emissionData['scope 1'][1][1],
    scope2: emissionData['Scope 2'][1][1],
    scope3: emissionData['Scope 3'][1][1],
    totalC02: emissionData['Total CO2'][1][1],
    airPol: emissionData['SOX / NOX etc'][1][1],
    airPolName: emissionData['SOX / NOX etc'][1][0],
  });
  return chartData;
}

function buildChartData(
  data: EsgStressTestPayload,
  chartType:
    | 'e_stress_test'
    | 's_stress_test'
    | 'g_stress_test'
    | 'esg_stress_test',
  disordered: boolean,
): ChartDataType[] {
  const esgData = disordered ? data.disorderly_esg_data : data.esg_data;
  if (!esgData?.[chartType]) return [];

  return esgData[chartType].map((value) => ({
    name: value[0],
    score: value[1],
  }));
}

const ESGStressTests: FC<FundInfoComponentProps> = ({
  fundId,
  positionDate,
}) => {
  const stressTestQuery = useEsgStressTestQuery(fundId, positionDate);

  useAdditionalTitleBarSelect(
    {
      displayOnOverviewPage: true,
      titleBarKey: 'key1',
      titleBarTitle: 'Stress Test Data: ',
      values: [
        {
          label: 'Orderly Transition',
          value: 'Standard ESG Data',
        },
        {
          label: 'Disorderly Transition',
          value: 'Disorderly Esg Data',
        },
        {
          label: 'Stressed Emissions Costs',
          value: 'Stressed Emissions Costs',
        },
      ],
      aValueIsAlwaysSelected: true,
      currentValue: 'Disorderly Esg Data',
    },
    true,
    true,
  );
  const dataKey = useTitleBarSelect('key1') || 'Disorderly Esg Data';

  return (
    <QueryBoundary<EsgStressTestPageModel>
      isLoading={stressTestQuery.isLoading}
      isFetching={stressTestQuery.isFetching}
      isError={stressTestQuery.isError}
      error={stressTestQuery.error}
      data={
        stressTestQuery.data?.data &&
        (stressTestQuery.data.data.esg_data ||
          stressTestQuery.data.data.emission_data)
          ? stressTestQuery.data
          : null
      }
      loadingMessages={['Loading ESG Data...', '...']}
      errorMessage="No ESG Data Found..."
      noDataMessage="No ESG Data Found..."
    >
      {(pageModel) => (
        <Grid container spacing={2} style={{ padding: 8 }}>
          {dataKey === 'Stressed Emissions Costs' ? (
            <EmissionsCostsChart
              data={buildEmissionsChartData(pageModel.data)}
            />
          ) : (
            <>
              <ScoreStressTestChart
                data={buildChartData(
                  pageModel.data,
                  'e_stress_test',
                  dataKey === 'Disorderly Esg Data',
                )}
                chartTitle={
                  dataKey === 'Disorderly Esg Data'
                    ? 'Disorderly Environmental Score Stress Test P/L'
                    : 'Environmental Score Stress Test P/L'
                }
              />
              <ScoreStressTestChart
                data={buildChartData(
                  pageModel.data,
                  's_stress_test',
                  dataKey === 'Disorderly Esg Data',
                )}
                chartTitle={
                  dataKey === 'Disorderly Esg Data'
                    ? 'Disorderly Social Score Stress Test P/L'
                    : 'Social Score Stress Test P/L'
                }
              />
              <ScoreStressTestChart
                data={buildChartData(
                  pageModel.data,
                  'g_stress_test',
                  dataKey === 'Disorderly Esg Data',
                )}
                chartTitle={
                  dataKey === 'Disorderly Esg Data'
                    ? 'Disorderly Governance Score Stress Test P/L'
                    : 'Governance Score Stress Test P/L'
                }
              />
              <ScoreStressTestChart
                data={buildChartData(
                  pageModel.data,
                  'esg_stress_test',
                  dataKey === 'Disorderly Esg Data',
                )}
                chartTitle={
                  dataKey === 'Disorderly Esg Data'
                    ? 'Disorderly ESG Score Stress Test P/L'
                    : 'ESG Score Stress Test P/L'
                }
              />
            </>
          )}
        </Grid>
      )}
    </QueryBoundary>
  );
};

export default ESGStressTests;
